import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateActivityTypeMutation, useUpdateActivityTypeMutation } from '../../../../hooks/mutations/useActivityTypeMutation';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import { crudStatus } from '../../../../utils/enums';
import CustomTextInput from '../../../CustomTextInput';
import ToggleLine from '../../../ToggleLine';
import ModalBody from '../../ModalBody';
import { ActivityType } from '../../../../services/apiSLTP/dailyReports/activityTypes';
import DropDown, { OptionType } from '../../../DropDown';
import { getActivityTypeLabel } from '../../../../constants/labels';
import { activityTypeEnum } from '../../../../constants';

interface ModalProps {
    activityType: ActivityType | null;
    showEditModal: boolean;
    isEditMode?: boolean;
    setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
    refetch: () => void;
}

const UpdateActivityTypeModal: React.FC<ModalProps> = ({ activityType, showEditModal, setShowEditModal, isEditMode = true, refetch }) => {
    const { t } = useTranslation();

    const [designation, setDesignation] = useState(activityType?.designation || '');
    const [type, setType] = useState<OptionType | null>(
        activityType?.type ? { label: capitalizeFirstLetter(getActivityTypeLabel(activityType.type)), value: activityType.type.toString() } : null
    );
    const [isActive, setIsActive] = useState(activityType ? activityType?.crudStatus === crudStatus.OK : true);

    const { mutate: mutateActivityType } = useUpdateActivityTypeMutation({ onSuccess: refetch });
    const { mutate: addActivityType } = useCreateActivityTypeMutation({ onSuccess: refetch });

    const handleSubmit = () => {
        const activityTypeData = {
            designation,
            type: parseInt(type?.value || ''),
            crudStatus: isActive ? crudStatus.OK : crudStatus.INACTIVE,
        };
        if (isEditMode && activityType) {
            mutateActivityType({
                activityTypeId: activityType._id,
                data: activityTypeData,
            });
        } else {
            addActivityType(activityTypeData);
        }
    };

    const handleActiveChange = (checked: boolean) => setIsActive(checked);

    const isFormValid = () => {
        return designation.trim() !== '' && type;
    };

    return (
        <ModalBody
            title={t('activityType')}
            subTitle={isEditMode ? t('update') : t('add')}
            closeText={t('cancel')}
            okText={t('validate')}
            open={showEditModal}
            disabledOk={!isFormValid()}
            handleClose={() => setShowEditModal(false)}
            handleOk={handleSubmit}
        >
            <CustomTextInput value={designation} label={t('name')} onChange={setDesignation} required />
            <ToggleLine checked={isActive} onChange={handleActiveChange} title={capitalizeFirstLetter(t('active'))} />
            <DropDown
                inputLabel={t('type')}
                items={Object.values(activityTypeEnum).map((type) => ({
                    label: capitalizeFirstLetter(getActivityTypeLabel(type)),
                    value: type.toString(),
                }))}
                selectedItem={type}
                handleChange={(_, value) => setType(value)}
                required
            />
        </ModalBody>
    );
};

export default UpdateActivityTypeModal;
